import * as NewsletterSubscribeEvent from '@ori-events/newsletter-subscribe';
import { CheckCircle, Clear, ErrorCircle } from '@ori-ui/icons';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Typography,
} from '@ori-ui/mui';
import { createNewsletterSubscription, useApiClient } from '@ori/global-api-client-react';
import Cookies from 'js-cookie';
import type { ChangeEvent, FC } from 'react';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { newsletterSubscribeCookieKey, newsletterSubscribeCookieValue } from '../../constants';
import { useAppConfig, useFooterContext } from '../../hooks';
import { PrivacyPolicy } from './PrivacyPolicy';
import { NewsletterTitleIconType, TitleIcon } from './TitleIcon';

interface NewsletterDialogProps {
  open: boolean;
  handleCloseDialog: () => void;
  src: 'OnlineFooter' | 'OnlinePopup';
}

enum SubscriptionProcessStatus {
  Initial = 'Initial',
  Subscribed = 'Subscribed',
  Failed = 'Failed',
}

enum ButtonAction {
  None = 'None',
  Reset = 'Reset',
  Submit = 'Submit',
}

const PREFIX = 'NewsletterDialog';

const classes = {
  paperSize: `${PREFIX}-paperSize`,
  closeButton: `${PREFIX}-closeButton`,
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const Root = styled(Dialog)(({ theme }) => ({
  [`& .${classes.paperSize}`]: {
    width: 500,
    [theme.breakpoints.down('xs')]: {
      width: '100vw',
      maxWidth: 500,
      marginLeft: 0,
      marginRight: 0,
    },
  },

  [`& .${classes.closeButton}`]: {
    position: 'absolute',
    right: 0,
  },

  [`& .${classes.title}`]: {
    padding: theme.spacing(6, 3, 3, 3),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(6, 6, 3, 6),
    },
  },

  [`& .${classes.content}`]: {
    padding: theme.spacing(3, 3, 4, 3),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3, 6, 4, 6),
    },
  },

  [`& .${classes.actions}`]: {
    padding: theme.spacing(0, 3, 3, 3),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 6, 3, 6),
    },
  },
}));

export const NewsletterDialog: FC<NewsletterDialogProps> = ({ open, src, handleCloseDialog }) => {
  const { data } = useFooterContext();
  const globalApiClient = useApiClient();
  const { tenant } = useAppConfig();
  const [status, setStatus] = useState(SubscriptionProcessStatus.Initial);
  const [emailAddress, setEmailAddress] = useState('');
  const [isEmailWrong, setIsEmailWrong] = useState<boolean | undefined>(undefined);
  const [invalidEmailText, setInvalidEmailText] = useState(' ');
  const [doSubscribe, setDoSubscribe] = useState(false);

  const { submitDialog, subscribedDialog, errorDialog } = data?.newsletterSubsciptionConfiguration ?? {
    submitDialog: undefined,
    subscribedDialog: undefined,
    errorDialog: undefined,
  };

  const content = {
    title: submitDialog?.headline,
    icon: NewsletterTitleIconType.Submit,
    text: submitDialog?.text,
    showForm: true,
    showButton: true,
    buttonText: submitDialog?.subscribeButtonText,
    buttonAction: ButtonAction.Submit,
    ppText: submitDialog?.privacyPolicyText ?? '',
    ppLinkText: submitDialog?.privacyPolicyUrlText ?? '',
    ppUrl: submitDialog?.privacyPolicyUrl ?? '',
  };

  if (status === SubscriptionProcessStatus.Subscribed) {
    content.title = subscribedDialog?.headline;
    content.icon = NewsletterTitleIconType.Subscribe;
    content.text = subscribedDialog?.text;
    content.showForm = false;
    content.showButton = false;
    content.buttonText = '';
    content.buttonAction = ButtonAction.None;
    content.ppText = '';
    content.ppLinkText = '';
    content.ppUrl = '';
  }

  if (status === SubscriptionProcessStatus.Failed) {
    content.title = errorDialog?.headline;
    content.icon = NewsletterTitleIconType.Error;
    content.text = '';
    content.showForm = false;
    content.showButton = true;
    content.buttonText = errorDialog?.buttonText;
    content.buttonAction = ButtonAction.Reset;
    content.ppText = '';
    content.ppLinkText = '';
    content.ppUrl = '';
  }

  useEffect(() => {
    async function subscribeToNewsletter() {
      const analyticsEvent = new CustomEvent(NewsletterSubscribeEvent.Name);
      globalThis.dispatchEvent(analyticsEvent);

      const result = await createNewsletterSubscription(globalApiClient, {
        email: emailAddress,
        source: window.location.pathname,
        origin: src,
      });

      if (result.status === 200 && ['New', 'Subscribed', 'Pending'].includes(result.data.status)) {
        Cookies.set(newsletterSubscribeCookieKey, newsletterSubscribeCookieValue);
        setStatus(SubscriptionProcessStatus.Subscribed);
      } else {
        setStatus(SubscriptionProcessStatus.Failed);
      }
    }

    if (doSubscribe) {
      subscribeToNewsletter();
      setDoSubscribe(false);
    }
  }, [tenant, emailAddress, src, doSubscribe, globalApiClient]);

  function handleClose() {
    return () => {
      setStatus(SubscriptionProcessStatus.Initial);
      setIsEmailWrong(undefined);
      setInvalidEmailText(' ');
      handleCloseDialog();
    };
  }

  function validateEmail(email: string) {
    if (!email.length) {
      setIsEmailWrong(undefined);
      setInvalidEmailText(' ');

      return;
    }
    const isMailValid = z.string().email().safeParse(email).success;

    if (isMailValid) {
      setIsEmailWrong(false);
      setInvalidEmailText(' ');
    } else {
      setIsEmailWrong(true);
      setInvalidEmailText(submitDialog?.invalidEmailLabel ?? 'Invalid email');
    }
  }

  function handleOnChange() {
    return ({ target }: ChangeEvent<HTMLInputElement>) => {
      const email = target.value.trim();
      setEmailAddress(email);
      validateEmail(email);
    };
  }

  function handleOnBlur() {
    return () => validateEmail(emailAddress);
  }

  function handleButton(action: ButtonAction) {
    if (action === ButtonAction.Reset) {
      return () => {
        setStatus(SubscriptionProcessStatus.Initial);
        setIsEmailWrong(undefined);
        setInvalidEmailText(' ');
      };
    }

    return () => {
      if (!isEmailWrong) {
        setDoSubscribe(true);
      }
    };
  }

  let endAdornment = null;
  if (typeof isEmailWrong === 'undefined') {
    endAdornment = null;
  } else if (isEmailWrong) {
    endAdornment = (
      <InputAdornment position="end">
        <ErrorCircle color="error" />
      </InputAdornment>
    );
  } else {
    endAdornment = (
      <InputAdornment position="end">
        <CheckCircle color="inherit" />
      </InputAdornment>
    );
  }

  return (
    <Root
      open={open}
      aria-labelledby="newsletter-dialog"
      PaperProps={{
        className: classes.paperSize,
      }}
      onClose={handleClose()}
    >
      <div className={classes.closeButton}>
        <IconButton
          aria-label="close"
          size="large"
          onClick={handleClose()}
        >
          <Clear />
        </IconButton>
      </div>
      <DialogTitle
        className={classes.title}
        id="newsletter-dialog"
      >
        <TitleIcon icon={content.icon} />
        <Typography
          component="h3"
          variant="h5"
          align="center"
        >
          {content.title}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <DialogContentText>{content.text}</DialogContentText>
        {content.showForm ? (
          <TextField
            autoFocus
            fullWidth
            required
            value={emailAddress}
            id="e-mail"
            label={submitDialog?.emailLabel}
            helperText={invalidEmailText}
            type="email"
            error={isEmailWrong}
            InputProps={{ endAdornment }}
            onChange={handleOnChange()}
            onBlur={handleOnBlur()}
          />
        ) : null}
      </DialogContent>
      <DialogActions className={classes.actions}>
        {content.showButton ? (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={isEmailWrong !== false}
            onClick={handleButton(content.buttonAction)}
          >
            {content.buttonText}
          </Button>
        ) : null}
      </DialogActions>
      <PrivacyPolicy
        text={content.ppText}
        linkText={content.ppLinkText}
        url={content.ppUrl}
      />
    </Root>
  );
};
